import clone from 'ramda/es/clone';
import { ChestWallAlignmentSetting } from 'generated/graphql';
import type { StepConfiguration, ViewportConfigurationEntry } from './steppedHPTypes';
import { parseTempStepId } from './tempStepId';

/**
 * Gets a StepConfiguration by index from a stepped HP's steps array.
 * Returns null if index is out of bounds.
 */
export function getStepConfigurationByIndex(
  stepConfigurations: Nullable<StepConfiguration[]>,
  stepIndex: number
): StepConfiguration | null {
  if (!stepConfigurations || stepIndex < 0 || stepIndex >= stepConfigurations.length) {
    return null;
  }
  return stepConfigurations[stepIndex];
}

/** The required name for the first step in a stepped HP. */
export const BASE_STEP_NAME = 'Base Step';

/** Prefix for client-generated step smids (not valid backend UUIDs). */
export const CLIENT_STEP_SMID_PREFIX = 'step-';

/** Returns true if the smid is a client-generated placeholder (not persisted to backend). */
export function isClientGeneratedSmid(smid: Nullable<string>): boolean {
  return smid != null && smid.startsWith(CLIENT_STEP_SMID_PREFIX);
}

/** Returns the sub-step total for a step, or 0 if it has no sub-steps. */
export function calculateSubStepTotal(step: Nullable<StepConfiguration>): number {
  return step?.sub_steps?.length ?? 0;
}

const DEFAULT_CONFIG_DETAILS: StepConfiguration['config_details'] = {
  quadrant_view: false,
  cad_on: false,
  chest_wall_alignment: ChestWallAlignmentSetting.None,
};

/**
 * Creates a new step, optionally deep-cloning from a source step. When cloning, overrides smid and
 * display_name on the clone. When creating from scratch, uses the provided defaultLayout (defaults
 * to "2,2").
 */
export function createDefaultStep(options: {
  name: string;
  smid?: string | null;
  sourceStep?: StepConfiguration;
  defaultLayout?: string;
}): StepConfiguration {
  const {
    name,
    smid = `${CLIENT_STEP_SMID_PREFIX}${crypto.randomUUID()}`,
    sourceStep,
    defaultLayout = '2,2',
  } = options;

  if (sourceStep) {
    return {
      ...clone(sourceStep),
      smid,
      display_name: name,
    };
  }

  return {
    smid,
    display_name: name,
    layouts: { '0': defaultLayout },
    config_details: { ...DEFAULT_CONFIG_DETAILS },
    viewports: {},
    sub_steps: null,
  };
}

/**
 * Returns the default display name for a step about to be appended to `stepConfigurations`.
 *
 * Numbering starts at the positional name (`Step N+1`) and advances past any label already in use,
 * so a step renamed to a default-shaped label — e.g. a Base Step renamed to "Step 2" — can never be
 * duplicated by a later auto-generated name (VX-4620). Terminates because the set of taken labels
 * is finite.
 */
export function generateDefaultStepName(stepConfigurations: StepConfiguration[]): string {
  if (stepConfigurations.length === 0) return BASE_STEP_NAME;

  let stepNumber = stepConfigurations.length + 1;
  while (isStepNameDuplicate(`Step ${stepNumber}`, stepConfigurations)) {
    stepNumber++;
  }
  return `Step ${stepNumber}`;
}

/** Identity of the study/series/stack a viewport resolved to. */
type ResolvedSeries = Pick<ViewportConfigurationEntry, 'study_smid' | 'series_smid' | 'stack_smid'>;

/** Result of {@link preserveResolvedViewports}, carrying counts so callers can log what happened. */
export type PreserveResolvedViewportsResult = {
  steps: StepConfiguration[];
  /** Viewports that regained a study/series/stack from the previous state. */
  reattached: number;
  /** Viewports that declare a matching rule but still resolve to nothing. These render blank. */
  unresolved: number;
};

/** True when the viewport has no resolved series to render. */
function isUnresolved(viewport: ViewportConfigurationEntry): boolean {
  return viewport.study_smid == null && viewport.series_smid == null && viewport.stack_smid == null;
}

/**
 * Stable key for the rule a viewport uses to pick a series: its criteria plus the source text and
 * comparison index. Two viewports with the same rule resolve to the same series by definition, so
 * the key is safe to share resolutions across — including the members of a mirror group, which
 * carry identical rules on purpose.
 */
function viewportRuleKey(viewport: ViewportConfigurationEntry): string | null {
  const criteria = (viewport.criteria ?? [])
    .map((c) => `${c.category}=${c.value}`)
    .sort()
    .join('|');
  const sourceText = viewport.series_criteria_source_text?.trim() ?? '';
  // A viewport with neither criteria nor source text has no rule to match on; leave it alone.
  if (criteria === '' && sourceText === '') return null;
  return JSON.stringify([sourceText, viewport.comparison_index ?? null, criteria]);
}

/**
 * Re-attaches server-resolved study/series/stack smids to step viewports that lost them.
 *
 * Hanging protocols are stored criteria-only: every viewport's study_smid, series_smid and
 * stack_smid are null in the database, and it is the server-side match at case load that resolves
 * them against the studies actually open. The HP edit form works in the stored shape, so the steps
 * it flushes on save carry nulls too. Writing those straight into the viewer atom replaces the
 * resolved assignments with nothing, and every viewport in the affected steps renders blank until
 * the page is refreshed and the match runs again (VX-4764).
 *
 * The refetch that follows the save does not rescue this: `useHangingProtocol`'s
 * `__lastInitializedHPSmid` guard deliberately refuses to re-initialize the steps for an HP it has
 * already seen, so the resolved copy never reaches the atom.
 *
 * So carry the resolution forward here instead. A viewport is only filled in when it resolves to
 * nothing AND its rule is byte-identical to one the previous state had already resolved — if the
 * user retargeted a viewport in the form, its rule changed, and it is correctly left unresolved for
 * the next match rather than pinned to the series it used to show.
 */
export function preserveResolvedViewports(
  flushedSteps: StepConfiguration[],
  previousSteps: Nullable<StepConfiguration[]>
): PreserveResolvedViewportsResult {
  const resolvedByRule = new Map<string, ResolvedSeries>();
  for (const step of previousSteps ?? []) {
    for (const viewport of Object.values(step.viewports ?? {})) {
      if (isUnresolved(viewport)) continue;
      const key = viewportRuleKey(viewport);
      if (key == null || resolvedByRule.has(key)) continue;
      resolvedByRule.set(key, {
        study_smid: viewport.study_smid ?? null,
        series_smid: viewport.series_smid ?? null,
        stack_smid: viewport.stack_smid ?? null,
      });
    }
  }

  let reattached = 0;
  let unresolved = 0;

  const steps = flushedSteps.map((step) => {
    let stepChanged = false;
    const viewports: Record<string, ViewportConfigurationEntry> = {};

    for (const [viewportId, viewport] of Object.entries(step.viewports ?? {})) {
      // An intentionally cleared viewport is meant to be empty; leave it that way.
      if (!isUnresolved(viewport) || viewport.is_clear === true) {
        viewports[viewportId] = viewport;
        continue;
      }

      const key = viewportRuleKey(viewport);
      const resolved = key == null ? undefined : resolvedByRule.get(key);
      if (resolved == null) {
        if (key != null) unresolved++;
        viewports[viewportId] = viewport;
        continue;
      }

      viewports[viewportId] = { ...viewport, ...resolved };
      reattached++;
      stepChanged = true;
    }

    return stepChanged ? { ...step, viewports } : step;
  });

  return { steps, reattached, unresolved };
}

/**
 * Checks whether a step name is a duplicate within the given configurations, optionally excluding a
 * step by ID (useful when renaming).
 */
export function isStepNameDuplicate(
  name: string,
  stepConfigurations: StepConfiguration[],
  excludeStepId?: string
): boolean {
  const trimmed = name.trim().toLowerCase();
  // Resolve the index to exclude so we can handle both real smids and
  // synthetic `__index_N` identifiers used when smid is null.
  let excludeIndex = -1;
  if (excludeStepId != null) {
    const tempIndex = parseTempStepId(excludeStepId);
    if (tempIndex !== null) {
      excludeIndex = tempIndex;
    } else {
      excludeIndex = stepConfigurations.findIndex((s) => s.smid === excludeStepId);
    }
  }
  return stepConfigurations.some(
    (step, i) => i !== excludeIndex && (step.display_name ?? '').trim().toLowerCase() === trimmed
  );
}
